import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Globe, Link2, Check, X, Copy, ExternalLink, Loader2, Settings2, Lock, Crown, QrCode, Download, Trash2, RefreshCw, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL;

export const DominioTab = ({ restaurant, onSave }) => {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [slug, setSlug] = useState(restaurant?.slug || "");
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDomainSetup, setShowDomainSetup] = useState(false);
  const [domainInput, setDomainInput] = useState("");
  const [configuringDomain, setConfiguringDomain] = useState(false);
  const [domainStatus, setDomainStatus] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [billing, setBilling] = useState(null);
  const [autoRefreshCount, setAutoRefreshCount] = useState(0);

  const plan = restaurant?.subscription_plan || billing?.subscription_plan || "trial";
  const canCustomDomain = plan === "pro" || plan === "enterprise";

  const menuBaseUrl = typeof window !== "undefined" ? window.location.origin : "https://kaisosystem.com";
  const menuPathUrl = `${menuBaseUrl}/m/${slug || restaurant?.id || ""}`;
  const subdomainUrl = slug && slug.length >= 3 ? `https://${slug}.kaisosystem.com` : null;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    authAxiosRef.current.get("/subscription/billing").then(r => setBilling(r.data)).catch(() => {});
    fetchDomainStatus();
  }, []);

  // Auto-refresh domain status every 30s while pending_dns
  useEffect(() => {
    if (domainStatus?.status !== "pending_dns") return;
    const timer = setTimeout(() => {
      fetchDomainStatus();
      setAutoRefreshCount(c => c + 1);
    }, 30000);
    return () => clearTimeout(timer);
  }, [domainStatus?.status, autoRefreshCount]);

  const fetchDomainStatus = async () => {
    try {
      const res = await authAxiosRef.current.get("/restaurant/domain/status");
      setDomainStatus(res.data);
    } catch { }
  };

  const checkSlug = async (value) => {
    const clean = value.toLowerCase().replace(/[^a-z0-9\-]/g, "").replace(/--+/g, "-");
    setSlug(clean);
    if (clean.length < 3) { setSlugAvailable(null); return; }
    setChecking(true);
    try {
      const res = await axios.get(`${API}/api/v1/public/check-slug/${clean}`);
      setSlugAvailable(res.data.available || clean === restaurant?.slug);
    } catch { setSlugAvailable(false); }
    setChecking(false);
  };

  const handleSaveSlug = async () => {
    if (slug && slug.length < 3) { toast.error("El subdominio debe tener al menos 3 caracteres"); return; }
    setSaving(true);
    try {
      await authAxios.patch("/restaurant", { slug: slug || undefined });
      toast.success("Subdominio guardado");
      if (onSave) onSave();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Error guardando");
    }
    setSaving(false);
  };

  const handleConfigureDomain = async () => {
    if (!domainInput || !domainInput.includes(".")) { toast.error("Ingresa un dominio valido"); return; }
    setConfiguringDomain(true);
    try {
      const res = await authAxios.post("/restaurant/domain/configure", { domain: domainInput });
      const data = res.data;
      setDomainStatus(data);
      setShowDomainSetup(false);

      if (data.status === "active") {
        toast.success("Dominio configurado automaticamente!");
      } else {
        toast.info("Configure os registros DNS no provedor do seu dominio");
      }
      if (onSave) onSave();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Erro ao configurar dominio");
    }
    setConfiguringDomain(false);
  };

  const handleVerifyDomain = async () => {
    setVerifying(true);
    try {
      const res = await authAxios.post("/restaurant/domain/verify");
      setDomainStatus(prev => ({ ...prev, ...res.data }));
      if (res.data.verified) {
        toast.success("Dominio verificado e ativo!");
      } else {
        toast.info(res.data.message || "DNS ainda nao propagado");
      }
    } catch (err) {
      toast.error("Erro ao verificar");
    }
    setVerifying(false);
  };

  const handleRemoveDomain = async () => {
    setRemoving(true);
    try {
      await authAxios.delete("/restaurant/domain");
      setDomainStatus({ domain: null, status: "none" });
      toast.success("Dominio removido");
      if (onSave) onSave();
    } catch {
      toast.error("Erro ao remover dominio");
    }
    setRemoving(false);
  };

  const copyUrl = (url) => { navigator.clipboard.writeText(url); toast.success("Copiado"); };

  const statusBadge = (status) => {
    if (status === "active") return <Badge className="bg-emerald-100 text-emerald-700 gap-1"><CheckCircle2 className="w-3 h-3" />Ativo</Badge>;
    if (status === "pending_dns") return <Badge className="bg-amber-100 text-amber-700 gap-1"><Clock className="w-3 h-3" />Pendente DNS</Badge>;
    return <Badge variant="secondary">Nenhum</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="dominio-tab">

      {/* Link padrao do menu */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Link do Menu</CardTitle>
          <CardDescription>Link padrao gerado automaticamente para seu restaurante</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground">Slug do restaurante</Label>
            <div className="flex items-center gap-1 mt-1">
              <span className="text-sm text-muted-foreground font-mono bg-muted px-2 py-1 rounded-l border border-r-0 border-input">kaisosystem.com/m/</span>
              <Input value={slug} onChange={e => checkSlug(e.target.value)} placeholder="meu-restaurante" className="max-w-[200px] font-mono rounded-l-none" data-testid="slug-input" />
              <div className="w-8 flex items-center justify-center ml-2">
                {checking && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                {!checking && slugAvailable === true && <Check className="w-5 h-5 text-emerald-500" />}
                {!checking && slugAvailable === false && <X className="w-5 h-5 text-red-500" />}
              </div>
            </div>
          </div>

          {subdomainUrl && (
            <div>
              <Label className="text-xs text-muted-foreground">Subdominio exclusivo</Label>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2 mt-1">
                <Globe className="w-4 h-4 text-emerald-600 shrink-0" />
                <code className="flex-1 text-sm font-mono truncate text-emerald-800">{subdomainUrl}</code>
                <Button variant="ghost" size="sm" onClick={() => copyUrl(subdomainUrl)} data-testid="copy-subdomain-link"><Copy className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="sm" onClick={() => window.open(subdomainUrl, "_blank")} data-testid="open-subdomain-link"><ExternalLink className="w-3.5 h-3.5" /></Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">Subdominio ativo automaticamente. Compartilhe com seus clientes.</p>
            </div>
          )}

          <div>
            <Label className="text-xs text-muted-foreground">Link alternativo do menu</Label>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center gap-2 mt-1">
              <Link2 className="w-4 h-4 text-gray-500 shrink-0" />
              <code className="flex-1 text-sm font-mono truncate text-gray-600">{menuPathUrl}</code>
              <Button variant="ghost" size="sm" onClick={() => copyUrl(menuPathUrl)} data-testid="copy-menu-link"><Copy className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="sm" onClick={() => window.open(menuPathUrl, "_blank")} data-testid="open-menu-link"><ExternalLink className="w-3.5 h-3.5" /></Button>
            </div>
          </div>

          {/* QR Code */}
          <div className="border-t pt-4">
            <Label className="text-xs text-muted-foreground flex items-center gap-1.5"><QrCode className="w-3.5 h-3.5" />QR Code del Menu</Label>
            <div className="flex items-start gap-6 mt-3">
              <div className="bg-white border-2 border-gray-200 rounded-xl p-4 shadow-sm" id="qr-code-container" data-testid="menu-qr-code">
                <QRCodeSVG value={subdomainUrl || menuPathUrl} size={180} level="H" includeMargin={true} fgColor="#111827" />
                <p className="text-center text-[10px] text-gray-400 mt-2 font-mono truncate max-w-[180px]">{slug || restaurant?.name || "Menu"}</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium">QR Code de divulgacion</p>
                <p className="text-xs text-muted-foreground">Este QR code dirige al cliente a tu menu publico.</p>
                <Button variant="outline" size="sm" onClick={() => {
                  const svg = document.querySelector('#qr-code-container svg');
                  if (!svg) return;
                  const svgData = new XMLSerializer().serializeToString(svg);
                  const canvas = document.createElement('canvas');
                  canvas.width = 400; canvas.height = 400;
                  const ctx = canvas.getContext('2d');
                  const img = new Image();
                  img.onload = () => { ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 400, 400); ctx.drawImage(img, 0, 0, 400, 400); const a = document.createElement('a'); a.download = `qr-menu-${slug || 'restaurante'}.png`; a.href = canvas.toDataURL('image/png'); a.click(); };
                  img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                }} data-testid="download-qr-btn">
                  <Download className="w-3.5 h-3.5 mr-1.5" />Descargar PNG
                </Button>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSaveSlug} disabled={saving || (slug && slugAvailable === false)} className="gap-2" data-testid="save-slug-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {saving ? "Salvando..." : "Salvar Slug"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dominio Propio */}
      {!canCustomDomain && (!domainStatus?.domain || domainStatus.status === "none") ? (
        <Card className="border-dashed opacity-80">
          <CardContent className="py-8 text-center">
            <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7 text-amber-500" />
            </div>
            <h3 className="font-bold text-lg mb-1">Dominio propio</h3>
            <p className="text-sm text-muted-foreground mb-4">Usa tu propio dominio para el menu publico.</p>
            <div className="flex items-center justify-center gap-2 mb-4">
              <Badge className="bg-purple-100 text-purple-700">Profesional 79EUR/mes</Badge>
              <Badge className="bg-amber-100 text-amber-700">Premium 189EUR/mes</Badge>
            </div>
            <Button onClick={() => window.location.href = "/settings?tab=subscription"} className="bg-purple-600 hover:bg-purple-700 text-white gap-2" data-testid="upgrade-domain-btn">
              <Crown className="w-4 h-4" /> Hacer upgrade
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              Dominio propio
              <Badge className="bg-purple-100 text-purple-700 text-[10px]">Pro / Premium</Badge>
            </CardTitle>
            <CardDescription>Conecta tu dominio propio. El sistema configura todo automaticamente.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {domainStatus?.domain && domainStatus.status !== "none" ? (
              <>
                {/* Active domain */}
                <div className={`rounded-lg p-4 flex items-center justify-between ${domainStatus.status === "active" ? "bg-emerald-50 border border-emerald-200" : "bg-amber-50 border border-amber-200"}`}>
                  <div className="flex items-center gap-3">
                    <Globe className={`w-5 h-5 ${domainStatus.status === "active" ? "text-emerald-600" : "text-amber-600"}`} />
                    <div>
                      <p className={`font-semibold ${domainStatus.status === "active" ? "text-emerald-800" : "text-amber-800"}`}>{domainStatus.domain}</p>
                      <div className="mt-1">{statusBadge(domainStatus.status)}</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {domainStatus.status === "active" && (
                      <Button variant="outline" size="sm" onClick={() => window.open(`https://${domainStatus.domain}`, "_blank")} data-testid="visit-domain-btn">
                        <ExternalLink className="w-4 h-4 mr-1" />Visitar
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={handleRemoveDomain} disabled={removing} data-testid="remove-domain-btn">
                      {removing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                {/* DNS Instructions */}
                {domainStatus.status === "pending_dns" && domainStatus.dns_records?.length > 0 && (
                  <div className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                      <p className="font-semibold text-sm">Configurar DNS no seu provedor</p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Acesse o painel DNS do seu provedor (Cloudflare, GoDaddy, Namecheap, etc.) e adicione o registro abaixo:
                    </p>

                    {domainStatus.dns_records.filter(r => r.type === "CNAME").map((rec, i) => (
                      <div key={i} className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4 space-y-2.5 font-mono text-sm">
                        <div className="grid grid-cols-[80px_1fr] gap-2 items-center">
                          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tipo</span>
                          <span className="font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded w-fit">{rec.type}</span>
                        </div>
                        <div className="grid grid-cols-[80px_1fr] gap-2 items-center">
                          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Nome</span>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">{rec.name || "@"}</span>
                            <span className="text-xs text-muted-foreground">(raiz do domínio)</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-[80px_1fr] gap-2 items-center">
                          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Valor</span>
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="font-bold text-emerald-700 truncate">{rec.value}</span>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0" onClick={() => { navigator.clipboard.writeText(rec.value); toast.success("Copiado!"); }} title="Copiar">
                              <Copy className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground pt-1 border-t border-amber-200">
                          TTL: Automático ou 300 segundos
                        </p>
                      </div>
                    ))}

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                      <p className="font-semibold mb-1">Como adicionar no seu provedor:</p>
                      <ul className="space-y-0.5 list-disc ml-4">
                        <li><strong>Cloudflare:</strong> DNS → Records → Add record → CNAME, Nome: @, Target: {domainStatus.cname_target || "kaisosystem.com"}</li>
                        <li><strong>GoDaddy:</strong> DNS → Add → CNAME, Host: @, Points to: {domainStatus.cname_target || "kaisosystem.com"}</li>
                        <li><strong>Namecheap:</strong> Advanced DNS → Add Record → CNAME, Host: @, Value: {domainStatus.cname_target || "kaisosystem.com"}</li>
                      </ul>
                      <p className="mt-2 text-blue-600">Propagação: instantânea no Cloudflare · até 48h em outros provedores</p>
                    </div>

                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs text-emerald-700 flex items-center gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                      Seu subdomínio <strong>{slug}.kaisosystem.com</strong> continua ativo enquanto aguarda
                    </div>

                    <Button onClick={handleVerifyDomain} disabled={verifying} className="gap-2 w-full" data-testid="verify-domain-btn">
                      {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      {verifying ? "Verificando DNS..." : "Verificar DNS agora"}
                    </Button>
                    <p className="text-xs text-center text-muted-foreground">Verificação automática a cada 5 minutos</p>
                  </div>
                )}

                {domainStatus.status === "active" && domainStatus.method === "cloudflare_worker" && (
                  <p className="text-xs text-emerald-600 text-center">Configurado automaticamente via Cloudflare. SSL ativo.</p>
                )}
              </>
            ) : (
              <div className="text-center py-6">
                <Globe className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground mb-4">No tienes un dominio propio configurado</p>
                <Button onClick={() => { setDomainInput(""); setShowDomainSetup(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="setup-domain-btn">
                  Configurar Dominio Propio
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Domain Setup Dialog */}
      <Dialog open={showDomainSetup} onOpenChange={setShowDomainSetup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configurar dominio propio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Ingresa tu dominio. El sistema intentara configurar automaticamente via Cloudflare.
              Si el dominio esta en otro proveedor, instrucciones de DNS seran mostradas.
            </p>
            <div>
              <Label className="text-xs text-muted-foreground">Dominio</Label>
              <Input
                value={domainInput}
                onChange={e => setDomainInput(e.target.value.toLowerCase().replace(/^(https?:\/\/)?(www\.)?/i, "").split("/")[0])}
                placeholder="meurestaurante.com"
                className="font-mono mt-1"
                data-testid="domain-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Exemplo: meurestaurante.com (sem www ou https)</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800 font-medium mb-1">Como funciona:</p>
              <ol className="text-xs text-blue-700 space-y-1 list-decimal ml-4">
                <li>Insira seu dominio acima e clique "Configurar"</li>
                <li>O sistema gera as instrucoes de DNS automaticamente</li>
                <li>Copie o registro DNS e adicione no seu provedor (Cloudflare, GoDaddy, etc.)</li>
                <li>Apos a propagacao (ate 48h), seu dominio abrira o menu automaticamente</li>
              </ol>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDomainSetup(false)}>Cancelar</Button>
            <Button
              onClick={handleConfigureDomain}
              disabled={configuringDomain || !domainInput || !domainInput.includes(".")}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
              data-testid="confirm-domain-btn"
            >
              {configuringDomain ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
              {configuringDomain ? "Configurando..." : "Configurar Dominio"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
